import React from "react";
import type { ResearchDossierCaptureV2 } from "@shared/researchDossier";

export type DossierVersionSummary = {
  reportId: string;
  parentReportId: string | null;
  sequence: number;
  createdAt: string;
  status: string;
};

const answerTitles = {
  "what-happened": "What happened?",
  "why-it-matters": "Why could it matter?",
  "assets-exposed": "Which assets are exposed?",
  "what-changed": "What changed?",
  support: "What supports this answer?",
} as const;

function referenceLabel(kind: "evidence" | "calculation", id: string, digest: string) {
  return `${kind} ${id} · envelope ${digest.slice(0, 12)}`;
}

function safeSource(value: string | null) {
  if (!value || value.length > 2_048) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname && !url.username && !url.password ? url.toString() : null;
  } catch {
    return null;
  }
}

function timeLabel(value: { precision: string; value: string; timeZone?: string | null }) {
  return `${value.value}${value.precision === "local" && value.timeZone ? ` (${value.timeZone})` : ""} · ${value.precision}`;
}

export default function ResearchDossierDisplay({
  capture,
  versions = [],
}: {
  capture: ResearchDossierCaptureV2;
  versions?: DossierVersionSummary[];
}) {
  const admitted = new Set(capture.admission.admittedIds);
  const excludedEvidenceIds = new Set(capture.result.excluded
    .filter((item) => item.ref.envelopeDigest === capture.evidence.digest && item.ref.kind === "evidence")
    .map((item) => item.ref.id));
  const facts = capture.evidence.evidence.filter((item) => admitted.has(item.id) && !excludedEvidenceIds.has(item.id));
  const hasConflicts = capture.result.contradictory.length > 0;
  const benchmark = capture.question.benchmark;

  return <section className="analytics-panel" aria-label="Saved research dossier evidence">
    <p className="home-kicker">DETERMINISTIC DOSSIER · NO MODEL CALLS</p>
    <h2>Current quote and valuation coverage</h2>
    <p>As of {capture.question.asOf}, which current quote and valuation facts are supported for {capture.definition.instrument.listingSymbol}, what has changed from its previous saved assessment, and what remains unavailable?</p>

    <p><strong>Evidence status:</strong> {capture.result.status === "partial" ? "Partial coverage" : "Insufficient evidence"}. Valuation remains unavailable under this question.</p>
    <p><strong>Quote support:</strong> {capture.result.hypothesisState}. <strong>Quality check:</strong> {capture.result.invalidation.state.toUpperCase()}
      {capture.result.invalidation.reasonCodes.length > 0 ? ` · ${capture.result.invalidation.reasonCodes.join(", ")}` : ""}
    </p>
    {hasConflicts && <p role="status">Conflicting eligible quote evidence needs review. This does not disprove an investment thesis.</p>}

    <div className="reports-detail-evidence">
      <h3>Admitted facts</h3>
      {facts.length === 0 ? <p>No eligible target quote was admitted.</p> : <ul>
        {facts.map((item) => <li key={item.id}>
          <strong>{item.label}</strong> · {item.provider} · {item.metricLabel}: {item.value} {item.unit}{item.currency ? ` ${item.currency}` : ""} · {referenceLabel("evidence", item.id, capture.evidence.digest)}
          {item.instrument ? ` · ${item.instrument}` : ""} · {timeLabel(item.observedAt)}
          {safeSource(item.sourceUrl) && <a href={safeSource(item.sourceUrl)!} target="_blank" rel="noreferrer"> Source</a>}
        </li>)}
      </ul>}
      {capture.calculations.length > 0 && <>
        <h3>Version comparison</h3>
        {benchmark.status === "available" && benchmark.reportId
          ? <p>Compared with saved report <a href={`/reports?id=${encodeURIComponent(benchmark.reportId)}`}>{benchmark.reportId}</a> at its original cutoff.</p>
          : <p>{capture.changeExplanation.status === "first-version" ? "This is the first saved version. No earlier comparison is available." : "A prior comparison is unavailable."}</p>}
        <ul>{capture.calculations.map((calculation) => <li key={calculation.id}>
          {referenceLabel("calculation", calculation.id, capture.evidence.digest)} · {calculation.label}: {calculation.value} {calculation.unit} (current point minus prior point; not a return or performance measure). Inputs: {calculation.inputRefs.map((ref) => referenceLabel(ref.kind, ref.id, ref.envelopeDigest)).join(", ")}
        </li>)}</ul>
        {capture.changeExplanation.reasons.map((reason, index) => <p key={`${index}-${reason}`}>{reason}</p>)}
      </>}
      {capture.calculations.length === 0 && <>
        <h3>Version comparison</h3>
        <p>{capture.changeExplanation.status === "first-version" ? "This is the first saved version. No earlier comparison is available." : capture.changeExplanation.reasons.join(" ") || "No compatible prior fact is available for comparison."}</p>
        {benchmark.reportId && <p>Prior saved report: <a href={`/reports?id=${encodeURIComponent(benchmark.reportId)}`}>{benchmark.reportId}</a></p>}
      </>}

      <h3>Known gaps</h3>
      {capture.result.missing.length === 0 ? <p>No additional missing metric was recorded.</p> : <ul>
        {capture.result.missing.map((item, index) => <li key={`${item.metric}-${index}`}>{item.metric}: {item.reason}</li>)}
      </ul>}
      {capture.admission.excluded.length > 0 && <>
        <h3>R06 excluded evidence</h3>
        <ul>{capture.admission.excluded.map((item, index) => <li key={`${item.provider}-${item.metric ?? ""}-${index}`}>
          {item.provider}{item.metric ? ` · ${item.metric}` : ""}: {item.reason}
        </li>)}</ul>
      </>}
      {capture.result.excluded.length > 0 && <>
        <h3>Excluded by the dossier cutoff</h3>
        <p>The source clock is later than this saved question’s cutoff. Its numeric value is omitted.</p>
        <ul>{capture.result.excluded.map((item) => <li key={`${item.ref.envelopeDigest}-${item.ref.kind}-${item.ref.id}`}>
          {referenceLabel(item.ref.kind, item.ref.id, item.ref.envelopeDigest)}: {item.reason}
        </li>)}</ul>
      </>}
      {hasConflicts && <>
        <h3>Contradictory references</h3>
        <ul>{capture.result.contradictory.map((ref) => <li key={`${ref.envelopeDigest}-${ref.kind}-${ref.id}`}>
          {referenceLabel(ref.kind, ref.id, ref.envelopeDigest)}
        </li>)}</ul>
      </>}
    </div>

    <div className="reports-detail-evidence">
      <h3>Five Monitor answers</h3>
      {capture.result.answers.map((answer) => <article key={answer.id}>
        <h4>{answerTitles[answer.id]}</h4>
        {answer.status === "unavailable" && answer.reason && <p>{answer.reason}</p>}
        {answer.claims.map((claim) => <div key={claim.id}>
          <p>{claim.text}</p>
          {claim.refs.length > 0 && <ul aria-label={`Evidence references for ${claim.id}`}>
            {claim.refs.map((ref) => <li key={`${ref.envelopeDigest}-${ref.kind}-${ref.id}`}>
              {referenceLabel(ref.kind, ref.id, ref.envelopeDigest)}
            </li>)}
          </ul>}
        </div>)}
      </article>)}
    </div>

    {versions.length > 0 && <details className="reports-detail-evidence">
      <summary>Saved dossier versions ({versions.length})</summary>
      <ol>{versions.map((version) => <li key={version.reportId}>
        <a href={`/reports?id=${encodeURIComponent(version.reportId)}`}>Version {version.sequence}</a> · {version.status} · {version.createdAt}
        {version.parentReportId ? ` · follows ${version.parentReportId}` : " · first version"}
      </li>)}</ol>
    </details>}
  </section>;
}
